import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Palette,
  Lock,
  Terminal,
  Eraser,
  Sparkles,
  Plus,
  Edit3,
  RotateCcw,
  History,
  CheckCircle,
  AlertCircle,
  Save,
  Loader2,
  ChevronRight,
  LayoutTemplate,
  Box,
  RectangleHorizontal,
  CircleDot,
  ChevronDown,
  ChevronUp,
  CornerUpLeft,
  Tag,
  Trash2,
  ChevronsDownUp,
  FlaskConical,
  Wand2,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PromptCategory } from '@/types';
import {
  PLACEHOLDERS_BY_CATEGORY,
  findUnknownPlaceholders,
} from '@/lib/promptPlaceholders';

// ============================================================================
// Types
// ============================================================================

interface PromptTemplate {
  id: string;
  key: string;
  category: PromptCategory;
  label: string;
  content: string;
  default_content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface HistoryEntry {
  id: string;
  template_key: string;
  old_content: string;
  new_content: string;
  changed_by: string | null;
  changed_at: string;
  name: string | null;
}

// A resolved "version" derived from history entries
interface PromptVersion {
  id: string;           // unique id for React key — 'original' for the pre-history state
  historyId: string | null; // actual DB id, null for 'original'
  label: string;        // auto-generated fallback label
  name: string | null;  // optional user-defined name
  content: string;
  changedAt: string | null;
  isOriginal: boolean;
}

// ============================================================================
// Category metadata
// ============================================================================

interface CategoryMeta {
  label: string;
  description: string;
  icon: React.ReactNode;
}

const CATEGORY_META: Record<PromptCategory, CategoryMeta> = {
  style: {
    label: 'Stile',
    description: 'Einrichtungsstile (Modern, Luxury, Industrial)',
    icon: <Palette className="w-4 h-4" />,
  },
  constraint: {
    label: 'Basis-Constraints',
    description: 'Basis-Templates und strukturelle Vorgaben',
    icon: <Lock className="w-4 h-4" />,
  },
  aspect_ratio: {
    label: 'Seitenverhältnisse',
    description: 'Format-Anweisungen für die Bildausgabe',
    icon: <RectangleHorizontal className="w-4 h-4" />,
  },
  system: {
    label: 'System-Prompts',
    description: 'Übergeordnete Präambeln',
    icon: <Terminal className="w-4 h-4" />,
  },
  room_empty: {
    label: 'Raumtypen – Leeren',
    description: 'Prompts zum Leerräumen von Räumen',
    icon: <Eraser className="w-4 h-4" />,
  },
  room_staged: {
    label: 'Raumtypen – Inszenieren',
    description: 'Prompts zum Inszenieren von Räumen',
    icon: <Sparkles className="w-4 h-4" />,
  },
  verification: {
    label: 'QA / Überprüfung',
    description: 'Analyse- und Korrektur-Prompts für die Bildqualitätsprüfung',
    icon: <CheckCircle className="w-4 h-4" />,
  },
  stage_refinement: {
    label: 'Staging – Reprompt & Verfeinerung',
    description: 'Zweistufiger Reprompt (Klassifizierer + Rewrite) und die 4 Scope-Edit-Templates für „Verfeinern"',
    icon: <Wand2 className="w-4 h-4" />,
  },
  empty_consult: {
    label: 'Staging – Leerräum-Berater',
    description: 'Vision-Berater, der die Leerräum-Anweisung bildspezifisch ausformuliert',
    icon: <Eraser className="w-4 h-4" />,
  },
  floorplan_2d: {
    label: 'Grundriss – 2D',
    description: 'Prompts für 2D-Grundrissgenerierung',
    icon: <LayoutTemplate className="w-4 h-4" />,
  },
  floorplan_3d: {
    label: 'Grundriss – 3D',
    description: 'Prompts für 3D-Dollhouse-Visualisierung',
    icon: <Box className="w-4 h-4" />,
  },
  floorplan_refinement: {
    label: 'Grundriss Bearbeitung',
    description: 'Klassifizier-System-Prompt + Edit-Templates für Phase 2 Refinement',
    icon: <FlaskConical className="w-4 h-4" />,
  },
  floorplan_flooring: {
    label: 'Grundriss – Bodenbeläge',
    description: 'Stil-Beschreibungen für die einzelnen Bodenbelag-Optionen',
    icon: <LayoutTemplate className="w-4 h-4" />,
  },
  floorplan_text_handling: {
    label: 'Grundriss – Text-Handling',
    description: 'Anweisung, ob Text im Grundriss entfernt oder behalten wird',
    icon: <LayoutTemplate className="w-4 h-4" />,
  },
  floorplan_room_label: {
    label: 'Grundriss – Raumbezeichnungen',
    description: 'Anweisungen für Raum-Beschriftungs-Strategie (Labels vorhanden / eingebrannt / fehlend)',
    icon: <Tag className="w-4 h-4" />,
  },
  floorplan_refinement_clause: {
    label: 'Grundriss Bearbeitung – Klauseln',
    description: 'Bedingte Kurz-Anweisungen für Refinement (Mask-Clause, Source-Floorplan-Hint)',
    icon: <FlaskConical className="w-4 h-4" />,
  },
};

const CATEGORY_ORDER: PromptCategory[] = [
  'style',
  'constraint',
  'aspect_ratio',
  'system',
  'room_empty',
  'room_staged',
  'verification',
  'stage_refinement',
  'empty_consult',
  'floorplan_2d',
  'floorplan_flooring',
  'floorplan_text_handling',
  'floorplan_room_label',
  'floorplan_3d',
  'floorplan_refinement',
  'floorplan_refinement_clause',
];


// ============================================================================
// Helper: build version timeline from history entries
// Versions are displayed newest → oldest.
// history is already ordered by changed_at DESC (newest first).
// ============================================================================

function buildVersions(history: HistoryEntry[]): PromptVersion[] {
  if (history.length === 0) return [];

  const versions: PromptVersion[] = [];
  const total = history.length;

  // Newest entries first: history[0] is the most recent change
  history.forEach((entry, idx) => {
    const versionNumber = total - idx; // descending: total, total-1, ..., 1
    const date = new Date(entry.changed_at).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    versions.push({
      id: entry.id,
      historyId: entry.id,
      label: `Version ${versionNumber} · ${date}`,
      name: entry.name ?? null,
      content: entry.new_content,
      changedAt: entry.changed_at,
      isOriginal: false,
    });
  });

  // The oldest entry's old_content is the state before any recorded change
  const oldest = history[history.length - 1];
  versions.push({
    id: 'original',
    historyId: null,
    label: 'Ursprüngliche Version',
    name: null,
    content: oldest.old_content,
    changedAt: null,
    isOriginal: true,
  });

  return versions;
}

// ============================================================================
// PlaceholderChip
// ============================================================================

const PlaceholderChip: React.FC<{ token: string }> = ({ token }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-mono border border-primary/20">
    {token}
  </span>
);

// ============================================================================
// VersionList sub-component
// ============================================================================

interface VersionListProps {
  versions: PromptVersion[];
  currentContent: string;
  onRevert: (version: PromptVersion) => void;
  onRename: (historyId: string, name: string) => Promise<void>;
  onDeleteVersion: (historyId: string) => Promise<void>;
}

const VersionList: React.FC<VersionListProps> = ({
  versions,
  currentContent,
  onRevert,
  onRename,
  onDeleteVersion,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [isSavingRename, setIsSavingRename] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const startRename = (version: PromptVersion) => {
    setRenamingId(version.id);
    setRenameValue(version.name ?? '');
  };

  const commitRename = async (version: PromptVersion) => {
    if (!version.historyId) return;
    setIsSavingRename(true);
    await onRename(version.historyId, renameValue.trim());
    setIsSavingRename(false);
    setRenamingId(null);
  };

  if (versions.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">Noch keine Änderungen aufgezeichnet.</p>
    );
  }

  return (
    <div className="space-y-2">
      {versions.map((version, idx) => {
        const isCurrent = version.content === currentContent;
        const isExpanded = expandedId === version.id;
        const isRenaming = renamingId === version.id;
        const isDeletable = !version.isOriginal && idx !== 0; // not original, not newest
        const isDeleting = deletingId === version.id;
        const displayTitle = version.name || version.label;

        return (
          <div
            key={version.id}
            className={`rounded-lg border transition-colors ${
              isCurrent
                ? 'border-primary/30 bg-primary/5'
                : 'border-border bg-muted/20'
            }`}
          >
            {/* Header row */}
            <div className="flex items-start gap-2 px-3 py-2">
              {/* Version info */}
              <div className="flex-1 min-w-0">
                {isRenaming ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(version);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      placeholder="z.B. SEO-Optimierung, A/B Test v2..."
                      className="h-7 text-xs"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      className="h-7 px-2 text-xs flex-shrink-0"
                      onClick={() => commitRename(version)}
                      disabled={isSavingRename}
                    >
                      {isSavingRename ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <CheckCircle className="w-3 h-3" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs flex-shrink-0"
                      onClick={() => setRenamingId(null)}
                      disabled={isSavingRename}
                    >
                      ×
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-medium text-foreground">
                      {displayTitle}
                    </span>
                    {/* Show auto-label as subtitle when a custom name is set */}
                    {version.name && (
                      <span className="text-xs text-muted-foreground">
                        ({version.label})
                      </span>
                    )}
                    {isCurrent && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                        Im Editor
                      </span>
                    )}
                    {version.isOriginal && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                        Original
                      </span>
                    )}
                  </div>
                )}

                {/* Content preview (when not renaming and not expanded) */}
                {!isRenaming && !isExpanded && (
                  <p className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                    {version.content.substring(0, 80)}
                    {version.content.length > 80 && '…'}
                  </p>
                )}
              </div>

              {/* Actions */}
              {!isRenaming && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Delete button (only for intermediate versions) */}
                  {isDeletable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1 text-muted-foreground hover:text-destructive"
                      disabled={isDeleting}
                      onClick={async () => {
                        setDeletingId(version.id);
                        await onDeleteVersion(version.historyId!);
                        setDeletingId(null);
                      }}
                      title="Version löschen"
                    >
                      {isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </Button>
                  )}
                  {/* Rename button (not for 'original') */}
                  {!version.isOriginal && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={() => startRename(version)}
                      title="Version benennen"
                    >
                      <Tag className="w-3 h-3" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    onClick={() => setExpandedId(isExpanded ? null : version.id)}
                  >
                    {isExpanded ? (
                      <><ChevronUp className="w-3 h-3" /> Einklappen</>
                    ) : (
                      <><ChevronDown className="w-3 h-3" /> Ansehen</>
                    )}
                  </Button>
                  <Button
                    variant={isCurrent ? 'outline' : 'secondary'}
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    disabled={isCurrent}
                    onClick={() => onRevert(version)}
                    title={isCurrent ? 'Bereits im Editor geladen' : `Wiederherstellen: ${displayTitle}`}
                  >
                    <CornerUpLeft className="w-3 h-3" />
                    {isCurrent ? 'Aktiv' : 'Wiederherstellen'}
                  </Button>
                </div>
              )}
            </div>

            {/* Expanded content */}
            {isExpanded && (
              <div className="px-3 pb-3">
                <pre className="text-xs font-mono bg-background border border-border rounded p-3 whitespace-pre-wrap break-all max-h-60 overflow-y-auto overflow-x-auto">
                  {version.content}
                </pre>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// Main component
// ============================================================================

interface AdminPromptConfigViewProps {
  categories?: PromptCategory[];
}

export const AdminPromptConfigView: React.FC<AdminPromptConfigViewProps> = ({ categories }) => {
  const { user } = useAuth();

  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<PromptCategory>(
    categories && categories.length > 0 ? categories[0] : 'style'
  );

  // Edit dialog state
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [editLabel, setEditLabel] = useState('');
  const [editKey, setEditKey] = useState('');
  const [editCategory, setEditCategory] = useState<PromptCategory>('room_empty');
  const [editContent, setEditContent] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);

  // History / versions
  const [editHistory, setEditHistory] = useState<HistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [revertedFromLabel, setRevertedFromLabel] = useState<string | null>(null);
  const [versionName, setVersionName] = useState('');

  // Reset confirmation
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Squash confirmation
  const [showSquashConfirm, setShowSquashConfirm] = useState(false);
  const [isSquashing, setIsSquashing] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ========================================================================
  // Data fetching
  // ========================================================================

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      let query = supabase
        .from('prompt_templates')
        .select('*')
        .order('category')
        .order('label');
      if (categories && categories.length > 0) {
        query = query.in('category', categories);
      }
      const { data, error } = await query;

      if (error) throw error;
      setTemplates((data as PromptTemplate[]) || []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  }, [categories]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchHistory = async (templateKey: string) => {
    setIsLoadingHistory(true);
    try {
      const { data } = await supabase
        .from('prompt_template_history')
        .select('*')
        .eq('template_key', templateKey)
        .order('changed_at', { ascending: false })
        .limit(20);
      setEditHistory((data as HistoryEntry[]) || []);
    } catch {
      setEditHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // ========================================================================
  // Derived versions list
  // ========================================================================

  const versions = useMemo(() => buildVersions(editHistory), [editHistory]);

  // ========================================================================
  // Dialog handlers
  // ========================================================================

  const openEdit = (template: PromptTemplate) => {
    setEditingTemplate(template);
    setIsNewTemplate(false);
    setEditLabel(template.label);
    setEditKey(template.key);
    setEditCategory(template.category);
    setEditContent(template.content);
    setEditIsActive(template.is_active);
    setEditHistory([]);
    setRevertedFromLabel(null);
    setVersionName('');
    setSaveError(null);
    setSaveSuccess(false);
    setShowResetConfirm(false);
    setShowSquashConfirm(false);
    fetchHistory(template.key);
  };

  const openNew = () => {
    setEditingTemplate(null);
    setIsNewTemplate(true);
    setEditLabel('');
    setEditKey('');
    setEditCategory(selectedCategory);
    setEditContent('');
    setEditIsActive(true);
    setEditHistory([]);
    setRevertedFromLabel(null);
    setVersionName('');
    setSaveError(null);
    setSaveSuccess(false);
    setShowResetConfirm(false);
  };

  const closeDialog = () => {
    setEditingTemplate(null);
    setIsNewTemplate(false);
    setRevertedFromLabel(null);
    setVersionName('');
    setSaveError(null);
    setSaveSuccess(false);
    setShowResetConfirm(false);
    setShowSquashConfirm(false);
  };

  const handleRevert = (version: PromptVersion) => {
    setEditContent(version.content);
    setRevertedFromLabel(version.name || version.label);
    setShowResetConfirm(false);
  };

  const handleRename = async (historyId: string, name: string) => {
    const { error } = await supabase
      .from('prompt_template_history')
      .update({ name: name || null })
      .eq('id', historyId);

    if (!error) {
      setEditHistory((prev) =>
        prev.map((e) => (e.id === historyId ? { ...e, name: name || null } : e))
      );
    }
  };

  const handleDeleteVersion = async (historyId: string) => {
    const { error } = await supabase
      .from('prompt_template_history')
      .delete()
      .eq('id', historyId);

    if (!error) {
      setEditHistory((prev) => prev.filter((e) => e.id !== historyId));
    }
  };

  const handleSquash = async () => {
    if (!editingTemplate) return;
    setIsSquashing(true);

    const originalVersion = versions.find((v) => v.isOriginal);
    const originalContent = originalVersion?.content ?? editingTemplate.default_content;

    // Delete all history entries for this template
    const { error: deleteError } = await supabase
      .from('prompt_template_history')
      .delete()
      .eq('template_key', editingTemplate.key);

    if (!deleteError) {
      // Re-insert a single entry: original → current live content
      await supabase.from('prompt_template_history').insert({
        template_key: editingTemplate.key,
        old_content: originalContent,
        new_content: editingTemplate.content,
        changed_by: user?.id ?? null,
        name: 'Squashed',
      });
      await fetchHistory(editingTemplate.key);
    }

    setIsSquashing(false);
    setShowSquashConfirm(false);
  };

  // ========================================================================
  // Save handler
  // ========================================================================

  const handleSave = async () => {
    setSaveError(null);
    setIsSaving(true);

    try {
      const targetCategory: PromptCategory = isNewTemplate
        ? editCategory
        : editingTemplate?.category ?? editCategory;
      const unknownPlaceholders = findUnknownPlaceholders(editContent, targetCategory);
      if (unknownPlaceholders.length > 0) {
        setSaveError(
          `Prompt enthält unbekannte Platzhalter: ${unknownPlaceholders.join(', ')}. ` +
            'Diese werden im Code nicht ersetzt und würden als Roh-Text an das KI-Modell gesendet. ' +
            'Bitte entferne sie oder wähle eine passende Kategorie.',
        );
        return;
      }

      if (isNewTemplate) {
        const keyTrimmed = editKey.trim();
        if (!keyTrimmed || !editLabel.trim() || !editContent.trim()) {
          setSaveError('Schlüssel, Bezeichnung und Inhalt sind Pflichtfelder.');
          return;
        }
        if (!/^[a-z0-9_]+$/.test(keyTrimmed)) {
          setSaveError('Schlüssel darf nur Kleinbuchstaben, Ziffern und _ enthalten.');
          return;
        }

        const { error } = await supabase.from('prompt_templates').insert({
          key: keyTrimmed,
          category: editCategory,
          label: editLabel.trim(),
          content: editContent,
          default_content: editContent,
          is_active: editIsActive,
        });
        if (error) throw error;
      } else if (editingTemplate) {
        // Record history if content changed
        if (editContent !== editingTemplate.content) {
          const { error: histErr } = await supabase
            .from('prompt_template_history')
            .insert({
              template_key: editingTemplate.key,
              old_content: editingTemplate.content,
              new_content: editContent,
              changed_by: user?.id ?? null,
              name: versionName.trim() || null,
            });
          if (histErr) {
            console.warn('[AdminPromptConfig] History insert failed:', histErr.message);
          }
        }

        const { error } = await supabase
          .from('prompt_templates')
          .update({
            label: editLabel.trim(),
            content: editContent,
            is_active: editIsActive,
          })
          .eq('key', editingTemplate.key);
        if (error) throw error;
      }

      setSaveSuccess(true);
      await fetchTemplates();
      setTimeout(() => {
        setSaveSuccess(false);
        closeDialog();
      }, 900);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  // ========================================================================
  // Quick active toggle (in list view)
  // ========================================================================

  const handleToggleActive = async (template: PromptTemplate) => {
    const { error } = await supabase
      .from('prompt_templates')
      .update({ is_active: !template.is_active })
      .eq('key', template.key);

    if (!error) {
      setTemplates((prev) =>
        prev.map((t) =>
          t.key === template.key ? { ...t, is_active: !t.is_active } : t
        )
      );
    }
  };

  // ========================================================================
  // Derived data
  // ========================================================================

  const countByCategory = (cat: PromptCategory) =>
    templates.filter((t) => t.category === cat).length;

  const filteredTemplates = templates.filter((t) => t.category === selectedCategory);

  const isModified = (t: PromptTemplate) => t.content !== t.default_content;

  const isDialogOpen = editingTemplate !== null || isNewTemplate;

  const hasUnsavedChanges = editingTemplate
    ? editContent !== editingTemplate.content ||
      editLabel !== editingTemplate.label ||
      editIsActive !== editingTemplate.is_active
    : editContent.trim().length > 0;

  // ========================================================================
  // Render
  // ========================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2 text-destructive">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <span className="text-sm">{loadError}</span>
        <Button variant="outline" size="sm" onClick={fetchTemplates} className="ml-auto">
          Erneut versuchen
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <LayoutTemplate className="w-5 h-5 text-primary" />
            Prompt-Konfiguration
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            KI-Prompts für die Bildgenerierung konfigurieren. Änderungen werden sofort aktiv.
          </p>
        </div>
        <Button onClick={openNew} size="sm">
          <Plus className="w-4 h-4 mr-1.5" />
          Neuer Prompt
        </Button>
      </div>

      {/* Layout: sidebar + content */}
      <div className="flex gap-5">
        {/* Category sidebar */}
        <div className="w-52 flex-shrink-0">
          <nav className="space-y-1">
            {(categories && categories.length > 0 ? categories : CATEGORY_ORDER).map((cat) => {
              const meta = CATEGORY_META[cat];
              const count = countByCategory(cat);
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${
                    isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {meta.icon}
                    {meta.label}
                  </span>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full ${
                      isActive
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Template list */}
        <div className="flex-1 min-w-0">
          <Card>
            <CardContent className="p-0">
              {filteredTemplates.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <LayoutTemplate className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
                  <p className="text-sm">Keine Prompts in dieser Kategorie.</p>
                  <p className="text-xs mt-1">Klicke auf "+ Neuer Prompt" um einen hinzuzufügen.</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredTemplates.map((template) => (
                    <div
                      key={template.key}
                      className="flex items-center gap-4 px-4 py-3 hover:bg-muted/40 transition-colors"
                    >
                      {/* Active indicator */}
                      <button
                        onClick={() => handleToggleActive(template)}
                        className="flex-shrink-0"
                        title={template.is_active ? 'Aktiv – klicken zum Deaktivieren' : 'Inaktiv – klicken zum Aktivieren'}
                      >
                        <CircleDot
                          className={`w-4 h-4 ${
                            template.is_active ? 'text-emerald-500' : 'text-muted-foreground/40'
                          }`}
                        />
                      </button>

                      {/* Label + key */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-foreground">
                            {template.label}
                          </span>
                          {isModified(template) && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20">
                              Angepasst
                            </span>
                          )}
                          {!template.is_active && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              Inaktiv
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground font-mono mt-0.5 truncate">
                          {template.key}
                        </p>
                      </div>

                      {/* Updated at */}
                      <span className="text-xs text-muted-foreground hidden sm:block flex-shrink-0">
                        {new Date(template.updated_at).toLocaleDateString('de-DE')}
                      </span>

                      {/* Edit button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(template)}
                        className="flex-shrink-0 gap-1.5"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        Bearbeiten
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit / New Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        {/* Scrolling lives in an inner child: making the transformed (translate-centered),
            rounded DialogContent itself the scroller causes white compositing artifacts
            at the edges during fast momentum scrolls (same pattern as UpdateDetailModal). */}
        <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
            <DialogTitle>
              {isNewTemplate ? 'Neuer Prompt' : 'Prompt bearbeiten'}
            </DialogTitle>
            <DialogDescription>
              {isNewTemplate
                ? 'Erstelle einen neuen Prompt-Template-Eintrag.'
                : `Schlüssel: ${editingTemplate?.key}`}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 overflow-y-auto overscroll-contain px-6 py-2 space-y-4 min-w-0">
            {/* Key + Category (only editable for new) */}
            {isNewTemplate && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>
                    Schlüssel <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={editKey}
                    onChange={(e) => setEditKey(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                    placeholder="z.B. room_living_empty"
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Nur Kleinbuchstaben, Ziffern und _
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>
                    Kategorie <span className="text-destructive">*</span>
                  </Label>
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value as PromptCategory)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {CATEGORY_ORDER.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_META[cat].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Label */}
            <div className="space-y-1.5">
              <Label>
                Bezeichnung <span className="text-destructive">*</span>
              </Label>
              <Input
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                placeholder="z.B. Wohnzimmer – Leeren"
              />
            </div>

            {/* Revert notification */}
            {revertedFromLabel && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20 text-sm text-primary">
                <CornerUpLeft className="w-4 h-4 flex-shrink-0" />
                <span>
                  Wiederhergestellt aus: <strong>{revertedFromLabel}</strong> — Klicke Speichern um die Änderung zu übernehmen.
                </span>
                <button
                  onClick={() => setRevertedFromLabel(null)}
                  className="ml-auto text-primary/60 hover:text-primary"
                >
                  ×
                </button>
              </div>
            )}

            {/* Content */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>
                  Inhalt <span className="text-destructive">*</span>
                </Label>
                <span className="text-xs text-muted-foreground">
                  {editContent.length} Zeichen
                </span>
              </div>
              <Textarea
                value={editContent}
                onChange={(e) => {
                  setEditContent(e.target.value);
                  setRevertedFromLabel(null);
                }}
                className="font-mono text-sm min-h-[240px] resize-y"
                style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                placeholder="Prompt-Text eingeben..."
                spellCheck={false}
              />
              {/* Placeholder reference (per Kategorie) */}
              {(() => {
                const allowed = PLACEHOLDERS_BY_CATEGORY[editCategory];
                const unknown = findUnknownPlaceholders(editContent, editCategory);
                return (
                  <div className="space-y-1.5 mt-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">
                        {allowed.length === 0
                          ? 'Keine Platzhalter für diese Kategorie zulässig'
                          : 'Verfügbare Platzhalter:'}
                      </span>
                      {allowed.map((p) => (
                        <PlaceholderChip key={p} token={p} />
                      ))}
                    </div>
                    {unknown.length > 0 && (
                      <div className="flex items-start gap-2 p-2 rounded-md bg-destructive/10 border border-destructive/20">
                        <AlertCircle className="w-3.5 h-3.5 text-destructive flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-destructive">
                          Unbekannte Platzhalter im Inhalt: {unknown.join(', ')} — werden im Code
                          nicht ersetzt und müssen entfernt werden, bevor der Prompt gespeichert
                          werden kann.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between py-1">
              <div>
                <p className="text-sm font-medium text-foreground">Aktiv</p>
                <p className="text-xs text-muted-foreground">
                  Inaktive Prompts werden ignoriert (Fallback auf Hardcode).
                </p>
              </div>
              <button
                onClick={() => setEditIsActive((v) => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  editIsActive ? 'bg-primary' : 'bg-muted-foreground/30'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    editIsActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Reset to default (existing only) */}
            {!isNewTemplate && editingTemplate && (
              <div className="pt-1 border-t border-border">
                {!showResetConfirm ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowResetConfirm(true)}
                    disabled={editContent === editingTemplate.default_content}
                    className="gap-1.5"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Auf Standardwert zurücksetzen
                  </Button>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-amber-700 flex-1">
                      Inhalt wird auf den ursprünglichen Standardwert (hardcodiert) zurückgesetzt.
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setShowResetConfirm(false)}>
                      Abbrechen
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditContent(editingTemplate.default_content);
                        setRevertedFromLabel('Standardwert');
                        setShowResetConfirm(false);
                      }}
                    >
                      Zurücksetzen
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* A/B Test shortcut */}
            {!isNewTemplate && editingTemplate && (editingTemplate.category === 'room_empty' || editingTemplate.category === 'room_staged') && (
              <div className="pt-1 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-violet-600 border-violet-200 hover:bg-violet-50"
                  onClick={async () => {
                    const baseKey = editingTemplate.key;
                    const abKey = `${baseKey}_ab`;
                    const abLabel = `${editLabel} (Variante B)`;

                    // Check if AB variant already exists
                    const { data: existing } = await supabase
                      .from('prompt_templates')
                      .select('key')
                      .eq('key', abKey)
                      .limit(1);

                    if (existing && existing.length > 0) {
                      // Already exists — open it for editing
                      const { data: abTemplate } = await supabase
                        .from('prompt_templates')
                        .select('*')
                        .eq('key', abKey)
                        .single();
                      if (abTemplate) {
                        setEditingTemplate(abTemplate);
                        setEditLabel(abTemplate.label);
                        setEditContent(abTemplate.content);
                        setEditIsActive(abTemplate.is_active);
                        setEditCategory(abTemplate.category as PromptCategory);
                      }
                      return;
                    }

                    // Create AB variant copy
                    const { data: newTemplate, error } = await supabase
                      .from('prompt_templates')
                      .insert({
                        key: abKey,
                        category: editingTemplate.category,
                        label: abLabel,
                        content: editContent, // Copy current content
                        default_content: editContent,
                        is_active: true,
                      })
                      .select()
                      .single();

                    if (error) {
                      console.error('Failed to create A/B variant:', error);
                      return;
                    }

                    // Activate A/B test config with this prompt
                    await supabase
                      .from('ab_test_config')
                      .update({
                        is_active: true,
                        prompt_key_b: abKey,
                        prompt_key_a: null, // A = default
                        description: `A/B Test: ${editingTemplate.label} vs ${abLabel}`,
                      })
                      .not('id', 'is', null); // Update the single config row

                    // Open the new variant for editing
                    if (newTemplate) {
                      setEditingTemplate(newTemplate);
                      setEditLabel(newTemplate.label);
                      setEditContent(newTemplate.content);
                      setEditIsActive(true);
                      setEditCategory(newTemplate.category as PromptCategory);
                      setIsNewTemplate(false);
                      // Reload templates
                      fetchTemplates();
                    }
                  }}
                >
                  <FlaskConical className="w-3.5 h-3.5" />
                  A/B Test erstellen
                </Button>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Erstellt eine Kopie als Variante B und aktiviert den A/B Test automatisch.
                </p>
              </div>
            )}

            {/* Version history */}
            {!isNewTemplate && (
              <div className="pt-1 border-t border-border space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-foreground flex items-center gap-1.5">
                    <History className="w-4 h-4 text-muted-foreground" />
                    Versionen
                    {versions.length > 0 && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                        {versions.length}
                      </span>
                    )}
                  </h4>
                  {/* Squash button — only when there are intermediate versions to collapse */}
                  {versions.length > 2 && !showSquashConfirm && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1.5"
                      onClick={() => setShowSquashConfirm(true)}
                      disabled={isSquashing}
                    >
                      <ChevronsDownUp className="w-3 h-3" />
                      Squash
                    </Button>
                  )}
                </div>

                {/* Squash confirmation */}
                {showSquashConfirm && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-amber-700 flex-1">
                      Alle Zwischenversionen werden gelöscht. Nur Original und aktuelle Version bleiben erhalten.
                    </span>
                    <Button size="sm" variant="outline" onClick={() => setShowSquashConfirm(false)} disabled={isSquashing}>
                      Abbrechen
                    </Button>
                    <Button size="sm" onClick={handleSquash} disabled={isSquashing}>
                      {isSquashing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Squash'}
                    </Button>
                  </div>
                )}

                {isLoadingHistory ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Laden...
                  </div>
                ) : (
                  <VersionList
                    versions={versions}
                    currentContent={editContent}
                    onRevert={handleRevert}
                    onRename={handleRename}
                    onDeleteVersion={handleDeleteVersion}
                  />
                )}
              </div>
            )}

            {/* Error / success */}
            {saveError && (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {saveError}
              </div>
            )}
            {saveSuccess && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-600 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                Gespeichert!
              </div>
            )}
          </div>

          <DialogFooter className="shrink-0 flex-col sm:flex-col gap-3 px-6 pb-6 pt-4">
            {/* Version name input — only shown for existing templates with unsaved content changes */}
            {!isNewTemplate && editingTemplate && editContent !== editingTemplate.content && (
              <div className="flex items-center gap-2 w-full">
                <Tag className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <Input
                  value={versionName}
                  onChange={(e) => setVersionName(e.target.value)}
                  placeholder="Versionsname (optional) – z.B. SEO-Optimierung, A/B Test v2"
                  className="text-sm h-9"
                />
              </div>
            )}
            <div className="flex justify-end gap-2 w-full">
              <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
                Abbrechen
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges || saveSuccess}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Speichern
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
